import { DictionaryPathPopoverContent } from '@/components/DictionaryDisplay';
import {
  getDictionaryDataByType,
  getDictionaryDisplayName,
} from '@/services/admin/dictionaryData';
import type { TreeSelectProps } from 'antd';
import { Popover, TreeSelect } from 'antd';
import React, { useEffect, useState } from 'react';

export type DictionarySelectAllowLevel = 'any' | 'leaf';

export interface DictionarySelectProps
  extends Omit<
    TreeSelectProps,
    'treeData' | 'loading' | 'treeLine' | 'placeholder'
  > {
  /** 字典类型，如 'job_position', 'industry' */
  type: string;
  /** 是否只加载启用的项（默认 true） */
  isActive?: boolean;
  /** 是否多选（默认 false） */
  multiple?: boolean;
  /** 允许选择的层级：任意一级 / 仅叶子（默认 'any'） */
  allowSelectLevel?: DictionarySelectAllowLevel;
  /** 占位文案 */
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
}

function buildTreeData(
  nodes: API.DictionaryTreeResponse[],
  allowSelectLevel: DictionarySelectAllowLevel,
  mapById: Map<
    string,
    { node: API.DictionaryTreeResponse; parentChain: API.DictionaryTreeResponse[] }
  >,
): TreeSelectProps['treeData'] {
  return nodes.map((node) => {
    const hasChildren = Boolean(node.children?.length);
    const selectable = allowSelectLevel === 'leaf' ? !hasChildren : true;
    const pathNodes = [...(mapById.get(node.id)?.parentChain ?? []), node];
    const label = getDictionaryDisplayName(node);
    const title = (
      <Popover
        key={node.id}
        content={<DictionaryPathPopoverContent pathNodes={pathNodes} />}
        trigger="hover"
        placement="right"
        mouseEnterDelay={0.2}
        mouseLeaveDelay={0.1}
        overlayStyle={{ maxWidth: 420 }}
      >
        <span>{label}</span>
      </Popover>
    );
    return {
      title,
      value: node.id,
      key: node.id,
      selectable,
      children: hasChildren && node.children
        ? buildTreeData(node.children, allowSelectLevel, mapById)
        : undefined,
    };
  });
}

/**
 * 字典选择组件（与地区选择同机制）
 * 数据源：按 type 的字典树（带缓存），选项 hover 展示层级路径 + 每级多语言
 */
const DictionarySelect: React.FC<DictionarySelectProps> = ({
  type,
  isActive = true,
  value,
  onChange,
  multiple = false,
  allowSelectLevel = 'any',
  placeholder = '请选择',
  className,
  style,
  disabled,
  ...rest
}) => {
  const [data, setData] = useState<Awaited<ReturnType<typeof getDictionaryDataByType>> | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!type) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getDictionaryDataByType(type, isActive)
      .then(setData)
      .finally(() => setLoading(false));
  }, [type, isActive]);

  const treeData =
    data?.tree && data?.mapById
      ? buildTreeData(data.tree, allowSelectLevel, data.mapById)
      : [];

  return (
    <TreeSelect
      className={className}
      style={style}
      disabled={disabled}
      placeholder={placeholder}
      loading={loading}
      treeData={treeData}
      treeLine
      allowClear
      showSearch
      treeNodeFilterProp="title"
      value={value}
      onChange={onChange}
      multiple={multiple}
      {...rest}
    />
  );
};

export default DictionarySelect;
